<template>
  <lf-card class="mb-8 py-6">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <lf-h3>{{ $t("DEALS.EQUIPMENT_RENTAL.INSURANCE_INFO") }}</lf-h3>
        <button @click.prevent="toggleModal">
          <icon-base
            :icon="IconEdit"
            class="mr-2-5"
            :icon-color="UTIL_COLORS.DEFAULT"
          />
        </button>
      </div>
    </template>
    <template #default>
      <div
        v-if="
          isFieldVisible(INSURANCE_FIELD_IDS.insurance_has_rental_protection)
        "
        class="flex items-start justify-between border-b px-4 pb-4"
      >
        <p>
          {{ $t("DEALS.EQUIPMENT_RENTAL.INSURANCE_INFO_PROTECTION_PLAN_DESC") }}
        </p>
        <logical-indicator
          :value="
            !!fieldData[INSURANCE_FIELD_IDS.insurance_has_rental_protection]
          "
        />
      </div>
      <div class="grid grid-cols-3 gap-5 p-4">
        <div v-for="field in FIELDS_DETAILS" :key="field?.id">
          <workflow-field
            :id="field?.id?.replaceAll('_', '-')"
            :label="field?.title ?? ''"
            :value="field?.value ?? ''"
            click-to-copy
            is-business
          />
        </div>
      </div>
      <hr />
      <file-list
        v-if="isFieldVisible(INSURANCE_FIELD_IDS.insurance_certificate)"
        :files="insuranceFiles"
      />
    </template>
  </lf-card>
  <insurance-info-edit-modal
    v-if="isModalOpen"
    :files="insuranceFiles"
    :close="close"
  />
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { UTIL_COLORS } from "@/helpers/constants";
import {
  INSURANCE_FIELD_IDS,
  WORKFLOW_BLOCKS_IDS,
} from "@/helpers/constants/workflow";
import { formatAddress } from "@/helpers/formatting";
import { useDealFiles } from "@/hooks/deals";
import { useWorkflowFields } from "@/hooks/workflow";

import WorkflowField from "@/components/deals/WorkflowLive/WorkflowLiveField.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import FileList from "@/views/deals/components/equipmentRental/FileList.vue";
import InsuranceInfoEditModal from "@/views/deals/components/equipmentRental/InsuranceInfoEditModal.vue";

import type { IAddress } from "@/models/common";

const { isFieldVisible, fieldData } = useWorkflowFields(
  WORKFLOW_BLOCKS_IDS.insurance
);
const { t } = useI18n();

const { getters } = useStore();
const { files } = useDealFiles();

const insuranceTypes = computed<Record<string, string>>(
  () => getters["options/insuranceTypes"]
);

const isModalOpen = ref(false);
const insuranceFiles = computed(
  () =>
    files.value?.filter((file) => file.file_type === "insurance_certificate") ||
    []
);
const FIELDS_DETAILS = computed(() =>
  [
    {
      id: INSURANCE_FIELD_IDS.insurance_company,
      title: t("DEALS.EQUIPMENT_RENTAL.INSURANCE_INFO_COMPANY"),
      value: fieldData.value[INSURANCE_FIELD_IDS.insurance_company],
    },
    {
      id: INSURANCE_FIELD_IDS.insurance_first_name,
      title: t("DEALS.EQUIPMENT_RENTAL.CONTACT_FIRST_NAME"),
      value: fieldData.value[INSURANCE_FIELD_IDS.insurance_first_name],
    },
    {
      id: INSURANCE_FIELD_IDS.insurance_last_name,
      title: t("DEALS.EQUIPMENT_RENTAL.CONTACT_LAST_NAME"),
      value: fieldData.value[INSURANCE_FIELD_IDS.insurance_last_name],
    },
    {
      id: INSURANCE_FIELD_IDS.insurance_telephone,
      title: t("DEALS.EQUIPMENT_RENTAL.CONTACT_PHONE"),
      value: fieldData.value[INSURANCE_FIELD_IDS.insurance_telephone],
    },
    {
      id: INSURANCE_FIELD_IDS.insurance_email_address,
      title: t("COMMON.EMAIL"),
      value: fieldData.value[INSURANCE_FIELD_IDS.insurance_email_address],
    },
    {
      id: INSURANCE_FIELD_IDS.insurance_address,
      title: t("COMMON.ADDRESS"),
      value: formatAddress(
        (fieldData.value[INSURANCE_FIELD_IDS.insurance_address] as IAddress) ||
          ""
      ),
    },
    {
      id: INSURANCE_FIELD_IDS.insurance_type,
      title: t("DEALS.EQUIPMENT_RENTAL.INSURANCE_TYPE"),
      value: fieldData.value[INSURANCE_FIELD_IDS.insurance_type]
        ? insuranceTypes.value[
            fieldData.value[INSURANCE_FIELD_IDS.insurance_type] as number
          ]
        : "-",
    },
  ].filter((field) => isFieldVisible(field.id))
);

const toggleModal = () => {
  isModalOpen.value = !isModalOpen.value;
};

const close = () => {
  isModalOpen.value = false;
};
</script>
